import { useTranslation } from 'react-i18next';

import { ArrowDown, ArrowUp, Mic2, Waves } from 'lucide-react';

import { freqToMidi } from '@muzly/voice-engine';

import { KeyboardRangeBar } from './charts';
import { ItemRow } from './ResultRow';
import {
  Card,
  SectionHeadCard,
  SortVerticalIcon,
} from '../../../../components';
import { octaveSpan } from '../../resultCopy';
import { estimateVoiceType, hasFinalConsonant } from '../../voiceTypes';

import type { DiagnosisRecord } from '../../record';

/**
 * 음역 (시안 §8.4 `994:6678`) — `내 전체 음역` 카드(건반 막대) + 요약 행(최저음·최고음·
 * 최대 음역·예상 보이스 타입·고음 전환).
 *
 * 시안의 나머지 두 막대(`편하게 사용할 수 있는 음역`·`내가 낸 음역`)는 그 값을 재는
 * 산식이 없어 그리지 않는다 — 같은 막대를 같은 값으로 세 번 그리면 셋이 다른 사실인
 * 것처럼 읽힌다.
 *
 * **고음 전환의 세 상태를 섞지 않는다**(registerTransition 모듈 주석): 필드 없음 = 측정
 * 불가(올라간 구간이 없었다), `[]` = 끊김 없이 올라갔다, 길이 = 끊긴 횟수. `?? []`로 뭉개면
 * 미측정이 "0회"로 둔갑해 화면이 안 잰 것을 잘했다고 말한다.
 */

const registerNote = (breaks: number[] | undefined): string => {
  if (breaks == null) {
    return 'web.result.range.registerPending';
  }
  return breaks.length === 0
    ? 'web.result.range.registerNone'
    : 'web.result.range.registerCount';
};

const Value = ({ main, sub }: { main: string; sub?: string | null }) => (
  <div className="flex flex-col items-end gap-1 text-right">
    <span className="text-base font-semibold tabular-nums text-bk-900">
      {main}
    </span>
    {sub && <span className="text-xs text-bk-600">{sub}</span>}
  </div>
);

export const RangeSection = ({ record }: { record: DiagnosisRecord }) => {
  const { t } = useTranslation();
  const range = record.range;
  const gender =
    typeof record.survey.gender === 'string' ? record.survey.gender : undefined;
  const voiceType = estimateVoiceType({ range, gender });
  const span = range ? octaveSpan(range.semitones) : null;
  // 막대는 MIDI 축이다. 음이름(`G2`)이 아니라 **Hz에서** 되돌린다 — 음이름은 이미 반올림된
  // 값이라 거기서 다시 만들면 반음 경계에서 한 칸씩 밀린다.
  const band = range
    ? { low: freqToMidi(range.lowHz), high: freqToMidi(range.highHz) }
    : null;
  // 조사를 고르려면 **번역된** 이름이 있어야 한다 — 키를 이어 붙이면 받침 판정이 키를 본다.
  const singers = voiceType?.singerKeys.map((key) => t(key)).join(', ');
  const iconClass = 'h-5 w-5 text-primary';
  const pending = t('web.result.pending');

  return (
    <div className="flex flex-col gap-6">
      <div className="px-[18px] py-4">
        <Card className="px-5 py-[18px]">
          <h3 className="text-lg font-semibold tracking-[-0.09px] text-bk-900">
            {t('web.result.range.wholeTitle')}
          </h3>
          <p className="mt-1.5 text-[13px] text-bk-700">
            {t('web.result.range.wholeDesc')}
          </p>
          {range && band ? (
            <>
              <p className="mt-6 text-center text-2xl font-bold tracking-[-0.12px] text-primary">
                {t('web.result.range.notes', {
                  low: range.lowNote,
                  high: range.highNote,
                })}
              </p>
              {span && (
                <p className="mt-1 text-center text-xs text-bk-600">
                  {t(`web.result.range.span.${span.key}`, {
                    octaves: span.octaves,
                  })}{' '}
                  {t('web.result.range.semitones', { value: range.semitones })}
                </p>
              )}
              <KeyboardRangeBar
                lowMidi={band.low}
                highMidi={band.high}
                lowLabel={range.lowNote}
                highLabel={range.highNote}
                legend={{
                  filled: t('web.result.range.legendFilled'),
                  empty: t('web.result.range.legendEmpty'),
                }}
              />
            </>
          ) : (
            // 못 잰 회차에는 창만 띄우지 않는다: 빈 막대는 "다 못 냈다"로 읽힌다.
            <p className="mt-6 text-center text-sm font-semibold text-bk-500">
              {pending}
            </p>
          )}
        </Card>
      </div>

      <div className="flex flex-col gap-6 py-4">
        <SectionHeadCard
          size="md"
          className="mx-[18px]"
          title={t('web.result.tabs.keyPoints')}
        />
        <div className="flex flex-col gap-2">
          <ItemRow
            icon={<ArrowDown className={iconClass} aria-hidden="true" />}
            title={t('web.result.range.lowest')}
            right={
              <Value
                main={range?.lowNote ?? pending}
                sub={
                  range &&
                  t('web.result.range.hz', { value: Math.round(range.lowHz) })
                }
              />
            }
          />
          <ItemRow
            icon={<ArrowUp className={iconClass} aria-hidden="true" />}
            title={t('web.result.range.highest')}
            right={
              <Value
                main={range?.highNote ?? pending}
                sub={
                  range &&
                  t('web.result.range.hz', { value: Math.round(range.highHz) })
                }
              />
            }
          />
          <ItemRow
            icon={<SortVerticalIcon className={iconClass} aria-hidden="true" />}
            title={t('web.result.range.widest')}
            right={
              <Value
                main={
                  span
                    ? t(`web.result.range.span.${span.key}`, {
                        octaves: span.octaves,
                      })
                    : pending
                }
                sub={
                  range &&
                  t('web.result.range.semitones', { value: range.semitones })
                }
              />
            }
          />
          <ItemRow
            icon={<Mic2 className={iconClass} aria-hidden="true" />}
            title={t('web.result.range.voiceType')}
            desc={
              singers
                ? `${t(
                    hasFinalConsonant(singers)
                      ? 'web.result.range.similar.gwa'
                      : 'web.result.range.similar.wa',
                    { singers },
                  )} ${t('web.result.range.similarNote')}`
                : null
            }
            right={<Value main={voiceType ? t(voiceType.labelKey) : pending} />}
          />
          <ItemRow
            icon={<Waves className={iconClass} aria-hidden="true" />}
            title={t('web.result.range.register')}
            desc={t(registerNote(record.registerBreaks), {
              count: record.registerBreaks?.length ?? 0,
            })}
            right={
              <Value
                main={
                  record.registerBreaks == null
                    ? pending
                    : t('web.result.range.registerTimes', {
                        count: record.registerBreaks.length,
                      })
                }
              />
            }
          />
        </div>
      </div>
    </div>
  );
};
